import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { resolveUploadsUrl } from './staticUrl';

export const LOGO_BLANCO_SRC = '/logo y texto cadencya blanco.png';
/** Color corporativo Cadencya (purple.900) */
export const CORPORATE_RGB = [88, 28, 135];
export const HEADER_HEIGHT_MM = 26;

export function safeFilenamePart(value) {
  return (String(value || 'banda')
    .trim()
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 60) || 'banda');
}

export function dateStamp() {
  return new Date().toISOString().slice(0, 10);
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('FileReader'));
    reader.readAsDataURL(blob);
  });
}

function loadImageFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo decodificar la imagen'));
    img.src = dataUrl;
  });
}

function mimeToPdfFormat(mime) {
  return /png/i.test(mime || '') ? 'PNG' : 'JPEG';
}

/** @param {Blob} blob */
export async function blobToPdfImageAsset(blob) {
  const dataUrl = await blobToDataUrl(blob);
  const format = mimeToPdfFormat(blob.type);
  const img = await loadImageFromDataUrl(dataUrl);
  return {
    dataUrl,
    format,
    width: img.naturalWidth || 1,
    height: img.naturalHeight || 1,
  };
}

export function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar la imagen'));
    img.src = src;
  });
}

async function loadPublicStaticImage(path) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const res = await fetch(`${origin}${encodeURI(path)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return blobToPdfImageAssetWithAlpha(blob);
}

/**
 * Reexporta la imagen como PNG para jsPDF.
 * @param {HTMLImageElement} img
 * @param {{ stripNearBlackBg?: boolean }} opts — JPEG del recortador deja el alfa en negro
 */
function imageElementToPngAsset(img, { stripNearBlackBg = false } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { alpha: true });
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  if (stripNearBlackBg) {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data } = imageData;
    const threshold = 42;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if (r <= threshold && g <= threshold && b <= threshold) {
        data[i + 3] = 0;
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }

  return {
    dataUrl: canvas.toDataURL('image/png'),
    format: 'PNG',
    width: img.naturalWidth || 1,
    height: img.naturalHeight || 1,
  };
}

/** @param {Blob} blob */
export async function blobToPdfImageAssetWithAlpha(blob) {
  const dataUrl = await blobToDataUrl(blob);
  const img = await loadImageFromDataUrl(dataUrl);
  const stripNearBlackBg = !/png|webp/i.test(blob.type || dataUrl);
  return imageElementToPngAsset(img, { stripNearBlackBg });
}

function fitAssetDimensions(asset, maxW, maxH) {
  const ratio = asset.width / asset.height || 1;
  let w = maxW;
  let h = w / ratio;
  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }
  return { w, h };
}

/** Carga imágenes de /uploads (fallback si no hay endpoint autenticado). */
export async function loadImageForPdfExport(src) {
  if (!src) return null;
  const abs = resolveUploadsUrl(src);
  if (!abs) return null;

  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const res = await fetch(abs, { mode: 'cors', credentials: 'include', headers });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    if (!blob.size) throw new Error('empty');
    return blobToPdfImageAssetWithAlpha(blob);
  } catch {
    try {
      const img = await loadImageElement(abs);
      return imageElementToPngAsset(img, { stripNearBlackBg: true });
    } catch {
      return null;
    }
  }
}

export function drawPdfBrandedHeader(doc, {
  listTitle,
  bandName,
  subtitle,
  logoAsset,
  bandLogoAsset,
  headerHeightMm,
}) {
  const pageW = doc.internal.pageSize.getWidth();
  const headerH = headerHeightMm ?? HEADER_HEIGHT_MM;

  doc.setFillColor(...CORPORATE_RGB);
  doc.rect(0, 0, pageW, headerH, 'F');

  if (logoAsset) {
    const { w: logoW, h: logoH } = fitAssetDimensions(logoAsset, 52, 12);
    const logoY = (headerH - logoH) / 2;
    doc.addImage(logoAsset.dataUrl, logoAsset.format, 10, logoY, logoW, logoH);
  }

  if (bandLogoAsset) {
    const { w, h } = fitAssetDimensions(bandLogoAsset, 58, headerH - 6);
    doc.addImage(
      bandLogoAsset.dataUrl,
      'PNG',
      (pageW - w) / 2,
      (headerH - h) / 2,
      w,
      h,
      undefined,
      'NONE',
    );
  }

  const textRightX = pageW - 10;
  const textMaxW = pageW * 0.32;

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(String(listTitle || 'Listado'), textRightX, 10, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  if (bandName) {
    doc.text(String(bandName), textRightX, 16, { align: 'right' });
  }

  if (subtitle) {
    doc.setFontSize(7.5);
    const lines = doc.splitTextToSize(String(subtitle), textMaxW);
    doc.text(lines, textRightX, 21, { align: 'right' });
  }

  return headerH;
}

/**
 * @param {{
 *   listTitle: string,
 *   columns: string[],
 *   rows: unknown[][],
 *   bandName?: string,
 *   subtitle?: string,
 *   filename?: string,
 *   columnStyles?: Record<number, { cellWidth?: number }>,
 *   defaultFilenamePrefix?: string,
 *   bandLogoAsset?: { dataUrl: string, format: string, width: number, height: number } | null,
 *   bandLogoInHeader?: boolean,
 *   orientation?: 'portrait' | 'landscape',
 *   tableStyles?: Record<string, unknown>,
 * }} params
 */
export async function downloadBrandedListadoPdf({
  listTitle,
  columns,
  rows,
  bandName,
  subtitle,
  filename,
  columnStyles,
  defaultFilenamePrefix = 'listado',
  bandLogoAsset = null,
  bandLogoInHeader = false,
  orientation = 'landscape',
  tableStyles,
}) {
  const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

  let cadencyaLogoAsset = null;
  try {
    cadencyaLogoAsset = await loadPublicStaticImage(LOGO_BLANCO_SRC);
  } catch {
    try {
      const img = await loadImageElement(LOGO_BLANCO_SRC);
      cadencyaLogoAsset = imageElementToPngAsset(img);
    } catch {
      cadencyaLogoAsset = null;
    }
  }

  const headerH = HEADER_HEIGHT_MM;
  const tableTopMargin = headerH + 4;
  const headerBandLogo =
    bandLogoInHeader && bandLogoAsset ? bandLogoAsset : null;

  autoTable(doc, {
    margin: { top: tableTopMargin, left: 10, right: 10 },
    head: [columns],
    body: rows,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: 'linebreak',
      ...tableStyles,
    },
    headStyles: {
      fillColor: CORPORATE_RGB,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [248, 245, 252],
    },
    ...(columnStyles ? { columnStyles } : {}),
    willDrawPage: (data) => {
      const pageSubtitle =
        data.pageNumber === 1 ? subtitle : `Página ${data.pageNumber}`;
      drawPdfBrandedHeader(doc, {
        listTitle,
        bandName,
        subtitle: pageSubtitle,
        logoAsset: cadencyaLogoAsset,
        bandLogoAsset: headerBandLogo,
        headerHeightMm: headerH,
      });
    },
  });

  const outName =
    filename || `${defaultFilenamePrefix}_${safeFilenamePart(bandName)}_${dateStamp()}.pdf`;
  doc.save(outName);
}

/**
 * @param {{
 *   listTitle: string,
 *   columns: string[],
 *   rows: unknown[][],
 *   bandName?: string,
 *   subtitle?: string,
 *   filename?: string,
 *   sheetName?: string,
 *   colWidths?: { wch: number }[],
 *   defaultFilenamePrefix?: string,
 * }} params
 */
export async function downloadBrandedListadoExcel({
  listTitle,
  columns,
  rows,
  bandName,
  subtitle,
  filename,
  sheetName = 'Listado',
  colWidths,
  defaultFilenamePrefix = 'listado',
}) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName.slice(0, 31) || 'Listado');
  ws.addRow([listTitle, bandName || '']);
  if (subtitle) ws.addRow(['', subtitle]);
  ws.addRow([]);
  ws.addRow(columns);
  for (const row of rows || []) {
    ws.addRow(Array.isArray(row) ? row : [row]);
  }
  if (colWidths?.length) {
    colWidths.forEach((c, i) => {
      if (c?.wch) ws.getColumn(i + 1).width = c.wch;
    });
  }
  const outName =
    filename || `${defaultFilenamePrefix}_${safeFilenamePart(bandName)}_${dateStamp()}.xlsx`;
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = outName;
  a.click();
  URL.revokeObjectURL(url);
}
